import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const OAuthRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const processOAuth = () => {
      console.log('🔍 [OAuth Redirect] Processing OAuth callback...');
      console.log('🔍 [OAuth Redirect] Current URL:', window.location.href);
      console.log('🔍 [OAuth Redirect] Search params:', Object.fromEntries(searchParams.entries()));
      console.log('🔍 [OAuth Redirect] Has opener:', !!window.opener);
      
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (error) {
        console.error('❌ [OAuth Redirect] OAuth Error:', error, errorDescription);
        setErrorMessage(errorDescription || error);
        setStatus('error');
        
        // Notify parent window of error
        if (window.opener) {
          console.log('📤 [OAuth Redirect] Notifying parent of error');
          window.opener.postMessage('canva_authorization_error', '*');
          setTimeout(() => window.close(), 3000);
        }
        return;
      }

      if (code && state) {
        console.log('✅ [OAuth Redirect] OAuth Success - Code received, processing...');
        console.log('🔑 [OAuth Redirect] Code:', code.substring(0, 10) + '...');
        console.log('🔑 [OAuth Redirect] State:', state);
        setStatus('success');
        
        // Store the authorization details for the auth service to pick up
        sessionStorage.setItem('canva_oauth_code', code);
        sessionStorage.setItem('canva_oauth_state', state);
        console.log('💾 [OAuth Redirect] Stored OAuth data in sessionStorage');
        
        // Notify parent window of success
        if (window.opener) {
          console.log('📤 [OAuth Redirect] Notifying parent of success');
          window.opener.postMessage('canva_authorization_success', '*');
          setTimeout(() => {
            console.log('🔒 [OAuth Redirect] Closing popup window');
            window.close();
          }, 2000);
        }
        return;
      }

      // No code or error - something went wrong
      console.error('❌ [OAuth Redirect] No authorization code or error received');
      setErrorMessage('No authorization code or error received');
      setStatus('error');
      
      if (window.opener) {
        console.log('📤 [OAuth Redirect] Notifying parent of error (no code)');
        window.opener.postMessage('canva_authorization_error', '*');
        setTimeout(() => window.close(), 3000);
      }
    };

    processOAuth();
  }, [searchParams]);

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <CardTitle className="text-xl text-gray-900 mb-2">
              Processing Authorization
            </CardTitle>
            <CardDescription>
              Please wait while we process your Canva authorization...
            </CardDescription>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-xl text-gray-900 mb-2">
              Authorization Successful!
            </CardTitle>
            <CardDescription>
              Successfully connected to Canva. This window will close automatically.
            </CardDescription>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-xl text-gray-900 mb-2">
              Authorization Failed
            </CardTitle>
            <CardDescription>
              {errorMessage || 'Failed to authorize with Canva. This window will close automatically.'}
            </CardDescription>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="pb-4">
          <div className="text-center">
            <h1 className="text-sm font-medium text-gray-500 mb-2">Canva OAuth</h1>
          </div>
        </CardHeader>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default OAuthRedirect;